<template>
  <div class="w-full max-w-[300px] mx-auto text-gray-500 text-2xl h-20">
    <Transition name="fade" mode="out-in">
      <div v-if="isLoading" class="w-full h-full flex flex-col items-center justify-center rounded-md bg-gray-100">
        <div>
          <IonSpinner class="size-6" color="primary" name="crescent"></IonSpinner>
        </div>
        <div class="text-xs italic font-medium">Loading Device Path...</div>
      </div>
      <div v-else-if="path.length && path.length > 3" class="w-full h-full flex items-center relative">
        <div class="absolute left-0 right-0 h-0.5 min-w-[10px] border-b-2 border-gray-300 border-dashed"></div>
        <div class="absolute inset-0 flex flex-row-reverse justify-between items-center">
          <i v-if="state?.childrenCount" class="icon-transceiver bg-white p-1"></i>
          <i class="bg-white p-1 text-blue-700" :class="path[0].icon"></i>
          <div class="size-8 rounded-full bg-gray-100 flex items-center justify-center text-lg font-bold">
            {{ path.length - 2 }}
          </div>
          <i class="bg-white p-1" :class="path[path.length - 1].icon"></i>
        </div>
        <div class="absolute left-0 right-0 text-xs font-medium bottom-0 text-center">
          {{ path.length - 1 }} hop{{ path.length - 1 > 1 ? 's' : '' }} from Gateway
        </div>
      </div>
      <div v-else-if="path.length > 1" class="w-full h-full flex items-center relative">
        <div class="absolute left-0 right-0 h-0.5 min-w-[10px] border-b-2 border-gray-300 border-dashed"></div>
        <div class="absolute inset-0 flex flex-row-reverse justify-between items-center">
          <i v-if="state?.childrenCount" class="icon-transceiver bg-white p-1"></i>
          <i v-for="d of path" class="bg-white p-1" :class="[d.icon, d.id === deviceId ? 'text-blue-700' : '']"></i>
        </div>
        <div class="absolute left-0 right-0 text-xs font-medium bottom-0 text-center">
          {{ path.length - 1 }} hop{{ path.length - 1 > 1 ? 's' : '' }} from Gateway
        </div>
      </div>
      <div
        v-else
        class="w-full h-full flex flex-col items-center justify-center text-sm italic font-medium text-gray-500"
      >
        Unable to resolve device path
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { DeviceService } from '@/http-services/device.service';
import { IonSpinner } from '@ionic/vue';
import { DeviceTypeHelper } from '@ncss/models';
import { useAsyncState } from '@vueuse/core';
import { computed, watch } from 'vue';

type Hop = { id: number; icon: string };

const props = defineProps<{
  deviceId: number;
}>();
const deviceService = new DeviceService();
function iconForDevice(id: number) {
  return DeviceTypeHelper.GetDeviceTypeBySerialNumber(id)?.iconName || 'icon-repeater';
}
const {
  state,
  execute: refresh,
  isLoading,
} = useAsyncState(() => deviceService.getDeviceTreeInfo(props.deviceId), undefined, {
  immediate: false,
  resetOnExecute: true,
});
watch(
  () => props.deviceId,
  (val, old) => {
    if (val !== old) {
      refresh();
    }
  },
  {
    immediate: true,
  }
);

const path = computed(() => {
  let p: Hop[] = [];
  let id: number | undefined | null = props.deviceId;
  while (id) {
    p.push({ id, icon: iconForDevice(id) });
    const found = state.value?.hopPath?.find((d) => d.id === id);
    if (!found) {
      break;
    }
    id = found.parentId;
    const existingIndex = p.findIndex((v) => v.id === id);
    if (existingIndex > -1) {
      p = p.slice(0, existingIndex + 1);
      break;
    }
  }
  return p;
});

defineExpose({
  state,
});
</script>

<style scoped></style>
